import { useEffect, useMemo, useState } from 'react'
import type { FC } from 'react'
import {
	Text,
	Flex,
	Button,
	Skeleton,
	Box,
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	ModalOverlay,
	Tabs,
	TabList,
	TabPanels,
	Tab,
	TabPanel,
	useDisclosure,
	Heading,
	Grid,
	Collapse,
	Tooltip,
	useBreakpointValue,
} from '@chakra-ui/react'
import type { ChakraProps } from '@chakra-ui/react'
import {
	Feature,
	InformationCircleBoldIcn,
} from '@alexanderathoodly/ui-library'
import {
	useSolutionDisplay,
	useFloorsWithBounds,
	useSolutionFloorId,
} from './utils'
import { formatInt, CoworkingSolution } from '@alexanderathoodly/data-models'
import type { LocationResult } from '@alexanderathoodly/data-models'
import SolutionMap from '../../maps/floorplanmap'
import {
	PLACEHOLDER_SRC,
	SIZE_410,
	SIZE_290,
	SIZE_1070,
} from '@yta/image-proxy'
import { GAAction, sendGA4Event } from '~/analytics/ga'
import { RequestShowingButton } from '~/forms/showingrequestform'
import { useQuery } from 'react-query'
import { getRoomQueries, servicesQuery } from './queries'
import { useParallelQueries } from '~/queries/useparallelqueries'
import { ImgProxy } from '~/utils/imgproxy'
import { useUser } from 'reactfire'
import {
	ShowPriceButton,
	ShowPriceModal,
	useShowPriceModal,
} from '~/forms/showpriceform'
import { useTranslation } from 'react-i18next'
import { useAIBot } from '~/aibotcontext'

export const InfoAboutBlurredPrice: FC = () => {
	const { t } = useTranslation()
	return (
		<Flex gap={2} alignItems="center">
			<InformationCircleBoldIcn
				backgroundColor="white"
				borderRadius="full"
				h={4}
				w={4}
				sx={{ path: { fill: 'blue.500' }, stroke: { fill: 'white' } }}
			/>
			<Text fontSize="xs">
				{t(
					'På yta.se kan du alltid se precis vad kontoret kostar - skapa ett kostnadsfritt konto för tillgång till samtliga priser'
				)}
			</Text>
		</Flex>
	)
}

const WorkspaceFilterTabs: FC<{
	openMapModal: (solution: CoworkingSolution) => void
}> = ({ openMapModal, ...rest }) => {
	const { t } = useTranslation()
	const { tabsData, firstValidTabIndex } = useSolutionDisplay()
	const { isOpen, onToggle } = useDisclosure()
	const [tabIndex, setTabIndex] = useState(firstValidTabIndex)
	useEffect(() => {
		setTabIndex(firstValidTabIndex)
	}, [firstValidTabIndex])

	if (!tabsData) {
		return (
			<>
				<Skeleton h={12} mb={10} />
				{[1, 2, 3, 4].map((item) => (
					<Skeleton key={item} h={24} mb={4} />
				))}
			</>
		)
	}

	if (tabsData.length == 0) {
		return (
			<Text color="grey.500">
				{t(
					'Det här kontorshotellet har tyvärr ingen tillgänglig lösning för tillfället. Kontaka oss gärna för att få mer information om kontorshotellet eller önska visning.'
				)}
			</Text>
		)
	}

	return (
		<Tabs
			variant="soft-rounded"
			onChange={(index) => setTabIndex(index)}
			defaultIndex={firstValidTabIndex >= 0 ? firstValidTabIndex : 0}
			{...rest}
			mt={1}
		>
			<Flex direction="column">
				<Text
					fontSize={{ base: 15, md: 'medium' }}
					mb={3}
					mr="auto"
					color="gray.500"
				>
					{t('Antal arbetsplatser')}
				</Text>
				<TabList gap="8px" display="flex" flexWrap="wrap">
					{tabsData.map((tab) => {
						return (
							<Tab
								key={`tab-${tab.label}`}
								isDisabled={!tab.solutions.length}
								onClick={() => {
									GAAction({
										category: 'site_interaction',
										action: 'toggle_solution_size_tab',
										metrics: {
											metric2: tab.minValue,
										},
									})
									sendGA4Event('site_interaction', {
										type: 'solution',
										sub_type: 'toggle_solution_size_tab',
									})
								}}
							>
								{tab.label}
							</Tab>
						)
					})}
				</TabList>
			</Flex>
			<TabPanels>
				{tabsData.map((tab, indexForTab) => {
					const solutionsLength = tab.solutions.length
					return (
						<TabPanel key={`tabpanel-${tab.label}`} px={0}>
							{solutionsLength > 0 &&
								tab.solutions.slice(0, 4).map((solution, index) => (
									<SolutionListItem
										key={solution.id}
										solution={solution}
										openMapModal={openMapModal}
										isShown={indexForTab == tabIndex}
										border={{
											md:
												solutionsLength <= 4 && solutionsLength == index + 1
													? 'none'
													: '',
										}}
									/>
								))}
							{solutionsLength > 4 && (
								<>
									<Collapse in={isOpen}>
										{tab.solutions.slice(4).map((solution) => (
											<SolutionListItem
												key={solution.id}
												solution={solution}
												openMapModal={openMapModal}
												isShown={indexForTab == tabIndex && isOpen}
											/>
										))}
									</Collapse>
									<Button variant="link" mt={7} w="100%" onClick={onToggle}>
										{isOpen
											? t('Visa färre lösningar')
											: t(
													'Visa {{rest_solutions}} ytterligare kontorsalternativ',
													{
														rest_solutions: tab.solutions.slice(4).length,
													}
											  )}
									</Button>
								</>
							)}
						</TabPanel>
					)
				})}
			</TabPanels>
		</Tabs>
	)
}

const ListItemMap: FC<{
	solution: CoworkingSolution | undefined
	visible: boolean
}> = ({ solution, visible }) => {
	const { location } = useSolutionDisplay()
	const [hasBeenShown, setHasBeenShown] = useState(false)
	const [solutionRoom, setSolutionRoom] = useState('')
	const solutionFloorId = useSolutionFloorId(solutionRoom)
	const floorsWithBounds = useFloorsWithBounds(location)

	useEffect(() => {
		if (!hasBeenShown && visible) {
			setHasBeenShown(true)
			//We get the solutions floor from a rooms data,
			//which we only want to get if the map is displayed
			if (solution && solution.rooms[0]) {
				setSolutionRoom(solution.rooms[0])
			}
		}
	}, [visible, hasBeenShown, solution])

	return !visible && !hasBeenShown ? null : (
		<SolutionMap
			solutionFloor={solutionFloorId || undefined}
			activeRooms={solution?.rooms}
			floors={floorsWithBounds}
		/>
	)
}

/**
 * Formats nicely the rent with a striked through price in case there is a discount, with the price/ws rounded to the closest hundred
 */
function useFormatRent(
	original: number,
	real: number,
	workspaces: number
): string | JSX.Element {
	const { t } = useTranslation()
	const realPriceDisplay = `${formatInt(real)} kr (${formatInt(
		Math.round(real / workspaces / 100) * 100
	)} ${t('kr/arbetsplats')})`
	return real === original ? (
		realPriceDisplay
	) : (
		<>
			<Box as="span" color="grey.300" textDecoration="line-through">
				{formatInt(original)} kr
			</Box>
			{realPriceDisplay}
		</>
	)
}

// There is description property in location, but we use this function to get a
// separate description because the description property is always empty.
function useSolutionDescription(
	location: LocationResult | undefined,
	solution: CoworkingSolution
): { title: string | undefined; content: string | undefined } {
	const { t } = useTranslation()
	const services = useQuery(servicesQuery)
	return useMemo(() => {
		if (services.isLoading || !location) {
			return {
				title: undefined,
				content: undefined,
			}
		}
		const workspaces = solution.workspaces
		if (solution.is_big_office) {
			return {
				title: t('Kontor'),
				content: t(
					'Kontorslösningen är en möblerad egen avdelning avsedd för upp till {{workspaces}} arbetsplatser. Inkluderat i hyran är medlemskap för {{workspaces}} personer.',
					{
						workspaces,
					}
				),
			}
		}
		const isFurnished = services.data?.find(
			(service) =>
				service.name == 'furnished' && location.services.includes(service.id)
		)
		const isRoomSingle = solution.rooms.length == 1
		return {
			title: t('Kontorsrum'),
			content: t(
				'Kontorslösningen består utav {{solution_rooms_length}} {{furnished}} kontorsrum för upp till {{workspaces}} arbetsplatser. Inkluderat i hyran är medlemskap för {{workspaces}} personer.',
				{
					solution_rooms_length: solution.rooms.length,
					furnished: isFurnished
						? isRoomSingle
							? t('möblerat')
							: t('möblerade')
						: '',
					workspaces,
				}
			),
		}
	}, [
		location,
		services.data,
		services.isLoading,
		solution.is_big_office,
		solution.rooms.length,
		solution.workspaces,
		t,
	])
}

function useRoomTypeDescription(solution: CoworkingSolution) {
	const { t } = useTranslation()
	return useMemo(
		() =>
			solution.is_big_office
				? {
						title: t('Egen avdelning'),
						content: t(
							'Egen avdelning innebär en hybrid mellan coworking och eget kontor för större team. En egen avdelning består av mer än ett rum samt har en entré och möjlighet till anpassning. Ofta finns det möjlighet att dela upp team, ha egna mötesrum, samt egen loungeyta. Access 24/7 och bindningstid från 6-12 månader.'
						),
				  }
				: {
						title: t('Privat kontor'),
						content: t(
							'Privat kontor innebär ett eller flera egna låsbara och möblerade kontorsrum som ni har access till 24/7. I hyran ingår full service samt tillgång till resterande del av kontorshotellets ytor så som loungeytor, mötesrum, arbetsplatser och pentry. Flexibla avtalsvillkor från 3-6 månader bindningstid.'
						),
				  },
		[solution.is_big_office, t]
	)
}

const SolutionListItem: FC<
	{
		solution: CoworkingSolution
		openMapModal: (solution: CoworkingSolution) => void
		isShown: boolean
	} & ChakraProps
> = ({ solution, openMapModal, isShown, ...rest }) => {
	const { status, data: user } = useUser()
	const { isAIBot } = useAIBot()
	const isToolTipDisabled = useBreakpointValue(
		{ base: true, md: false },
		{ ssr: true }
	)
	const { t } = useTranslation()
	const { location } = useSolutionDisplay()
	const { isOpen, onToggle } = useDisclosure()
	const { isOpen: isSolutionImagesOpen, onOpen, onClose } = useDisclosure()
	const { isShowPriceModalOpen, onShowPriceModalClose, onShowPriceModalOpen } =
		useShowPriceModal('solution', solution.id)
	const rooms = useParallelQueries(getRoomQueries(solution.rooms, isShown))
	const jointedRoomsName = useMemo(() => {
		if (!rooms.data) {
			return undefined
		}
		return rooms.data.length > 0
			? rooms.data.map((room) => room.external_id).join(' • ')
			: ''
	}, [rooms])
	const solutionDescription = useSolutionDescription(location, solution)
	const roomTypeDescription = useRoomTypeDescription(solution)
	const formattedRent = useFormatRent(
		solution.original_price,
		solution.price,
		solution.workspaces
	)
	const showPrices = !!user || isAIBot

	if (!location) {
		return <></>
	}

	return (
		<Grid
			as="article"
			templateColumns="auto 1fr auto"
			autoFlow="row"
			borderColor="grey.100"
			borderStyle="solid"
			borderBottomWidth="thin"
			py={3}
			gap="10px"
			_hover={{}}
			css={{ contentVisibility: isShown ? 'visible' : 'hidden' }}
			{...rest}
		>
			<Heading as="h3" fontSize="md" fontWeight="semibold" gridColumn="span 2">
				{solution.is_big_office
					? t('Egen Avdelning')
					: t('Privat Kontor ({{solution_rooms_length}} rum)', {
							solution_rooms_length: solution.rooms.length,
							count: solution.rooms.length,
					  })}
			</Heading>
			<Box position="relative" gridColumn="1">
				<ImgProxy
					sizeSet={{ base: SIZE_290 }}
					src={solution.thumbnail || PLACEHOLDER_SRC}
					loading="lazy"
					height={16}
					width="min-content"
					alt={
						solution.thumbnail
							? `
							Planritning för ${location ? location.name : solution.location}, ${
									solution.rooms.length
							  } privata rum, ${solution.size} m²`
							: `ingen tillgänglig bild`
					}
				/>
			</Box>
			<Grid templateRows="repeat(2,1fr)" templateColumns="repeat(2,1fr)">
				<Feature
					name={t('Arbetsplatser')}
					value={solution.workspaces.toString()}
				/>
				<Feature name={t('Storlek')} value={`${formatInt(solution.size)}m²`} />
				{/** TODO: Display floor once that is in solution data */}
				{/* <Feature name="Våning" value={solution.floorData?.name || 'NA'} /> */}
				{showPrices ? (
					<Feature gridColumn="span 2" name="Hyra/mån" value={formattedRent} />
				) : (
					<Flex gap="4px" gridColumn="span 2">
						<Text as="dt" color="grey.500">
							{t('Hyra/mån')}:
						</Text>
						<Tooltip
							label={<InfoAboutBlurredPrice />}
							maxW="max-content"
							borderRadius="5px"
							py={1}
							isDisabled={isToolTipDisabled || false}
						>
							<Flex gap={1}>
								<Text
									as="dd"
									fontWeight="semibold"
									color="grey.800"
									filter="blur(4px)"
									_hover={{ cursor: 'pointer' }}
									onClick={() => {
										onShowPriceModalOpen()
										sendGA4Event('initiate_price_account', {
											step: 'initiate',
											location: 'blurred price',
										})
									}}
								>
									12345
								</Text>
								<Text>{t('kr / mån')}</Text>
							</Flex>
						</Tooltip>
					</Flex>
				)}
			</Grid>
			<Box
				gridColumn={{ base: '1/-1', md: '1/4' }}
				mt={3}
				mb={6}
				hidden={!isOpen}
			>
				<Flex alignItems="center">
					<Text fontWeight="semibold">{solutionDescription.title}</Text>
					<Skeleton isLoaded={!!jointedRoomsName} h={5} w="30%" ml={1} flex={1}>
						<Text fontWeight="semibold">{jointedRoomsName}</Text>
					</Skeleton>
				</Flex>
				<Text color="grey.800">{solutionDescription.content}</Text>
				<Text mt={6} fontWeight="semibold">
					{roomTypeDescription.title}
				</Text>
				<Text color="grey.800">{roomTypeDescription.content}</Text>
			</Box>
			{status == 'loading' ? (
				<Skeleton w="100%" h={10} borderRadius="md" />
			) : showPrices ? (
				<RequestShowingButton
					solution={solution}
					display={{ base: isOpen ? 'inline-flex' : 'none', md: 'inline-flex' }}
					variant={isOpen ? 'solid' : 'outline'}
					gridColumn={{ base: '1/-1', md: '3/4' }}
					gridRow={{ base: 'auto', md: '2/3' }}
					mb={2}
				/>
			) : (
				<ShowPriceButton
					onClick={onShowPriceModalOpen}
					GA4EventLocation="solution list"
					display={{ base: isOpen ? 'inline-flex' : 'none', md: 'inline-flex' }}
					variant={isOpen ? 'solid' : 'outline'}
					gridColumn={{ base: '1/-1', md: '3/4' }}
					gridRow={{ base: 'auto', md: '2/3' }}
					mb={2}
				>
					{t('Se pris')}
				</ShowPriceButton>
			)}
			<ShowPriceModal
				location={location}
				solution={solution}
				isOpen={isShowPriceModalOpen}
				onClose={onShowPriceModalClose}
			/>
			<Button
				display={{ base: isOpen ? 'inline-flex' : 'none', md: 'none' }}
				gridColumn="1/-1"
				variant="outline"
				bg="#FFFFFF"
				mb={4}
				onClick={() => {
					openMapModal(solution)
					GAAction({
						category: 'engagement',
						action: 'select_solution',
						label: 'location_page_table',
					})
				}}
			>
				{t('Visa planritning')}
			</Button>
			{solution.images.length > 0 && (
				<Flex
					display={isOpen ? 'flex' : 'none'}
					gridColumn={{ base: '1/-1', md: '1/4' }}
					wrap="wrap"
					gap={{ base: '6px', md: '10px' }}
					hidden={!isOpen}
					direction="row"
					position="relative"
					mb={6}
				>
					{solution.images.slice(0, 3).map((image, index) => (
						<ImgProxy
							width="32%"
							key={`${image}-${index}`}
							sizeSet={{ base: SIZE_290 }}
							src={image || PLACEHOLDER_SRC}
							loading="lazy"
							alt={`room-${image}-${index}`}
							_hover={{ cursor: 'pointer' }}
							onClick={() => {
								onOpen()
								sendGA4Event('site_interaction', { type: 'pictures' })

								// Using timeout just to give the image element to be in the dom once the modal opens,
								// 'getElementById' was smoother than having ref's for all images.
								setTimeout(() => {
									const imageElement = document.getElementById(
										`solution-image-${image}`
									)
									if (!imageElement) {
										return
									}
									imageElement.scrollIntoView({
										behavior: 'smooth',
										block: 'end',
									})
								}, 300)
							}}
						/>
					))}
					{solution.images.length >= 3 && (
						<Button
							variant="outline"
							bg="white"
							size={{ base: 'sm', md: 'md' }}
							top={{ base: 4, md: 12, lg: 9 }}
							right={{ base: 3, md: 16, lg: 10 }}
							position="absolute"
							onClick={() => {
								onOpen()
								sendGA4Event('site_interaction', { type: 'pictures' })
							}}
						>
							{t('Alla bilder')}
						</Button>
					)}
					<Modal isOpen={isSolutionImagesOpen} onClose={onClose}>
						<ModalOverlay />
						<ModalContent maxW="6xl">
							<ModalHeader>{`${solutionDescription.title || ''}, ${
								location.name
							}`}</ModalHeader>
							<ModalCloseButton />
							<ModalBody display="flex" flexDirection="column" gap="50px">
								{solution.images.map((image, index) => (
									<ImgProxy
										id={`solution-image-${image}`}
										key={image}
										src={image}
										sizeSet={{ base: SIZE_410, md: SIZE_1070 }}
										w="100%"
										alt={`room-${image}-${index}`}
									/>
								))}
							</ModalBody>
						</ModalContent>
					</Modal>
				</Flex>
			)}
			{isOpen && (
				<Box gridColumn="1/4" display={{ base: 'none', md: 'block' }}>
					<ListItemMap solution={solution} visible={isOpen} />
				</Box>
			)}
			<Button
				variant="link"
				color="blue.500"
				size="sm"
				onClick={() => {
					onToggle()
					sendGA4Event('site_interaction', {
						type: 'solution',
						sub_type: 'toggle_show_more_button',
					})
				}}
				gridColumn="1/-1"
				justifySelf="end"
				width={{
					base: '100%',
					md: 'min-content',
				}}
			>
				{isOpen ? t('Visa mindre') : t('Visa mer')}
			</Button>
		</Grid>
	)
}

const SolutionMapModal: FC<{
	solution: CoworkingSolution | undefined
	isModalOpen: boolean
	closeModal: () => void
}> = ({ solution, closeModal, isModalOpen }) => {
	const { t } = useTranslation()
	return (
		<Modal isOpen={isModalOpen} onClose={closeModal}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>{t('Planritning_one')}</ModalHeader>
				<ModalCloseButton />
				<ModalBody
					display="flex"
					flexDirection="column"
					justifyContent="space-around"
					px={1}
				>
					<ListItemMap solution={solution} visible={true} />
				</ModalBody>
			</ModalContent>
		</Modal>
	)
}

export const SolutionsDisplay: FC = () => {
	const { isError, error, selectedSolution, selectSolution } =
		useSolutionDisplay()
	const [isModalOpen, setIsModalOpen] = useState(false)

	const openMapModal = (solution?: CoworkingSolution) => {
		if (solution) {
			selectSolution(solution)
		}
		setIsModalOpen(true)
	}

	if (isError) {
		return <span>Error: {(error as Error).message}</span>
	}

	return (
		<Flex display="flex" flexDirection="column">
			<WorkspaceFilterTabs openMapModal={openMapModal} />
			<SolutionMapModal
				solution={selectedSolution}
				isModalOpen={isModalOpen}
				closeModal={() => {
					if (selectedSolution) {
						selectSolution(undefined)
					}
					setIsModalOpen(false)
				}}
			/>
		</Flex>
	)
}
